import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatMoney } from '../lib/currency';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, User, Calendar, Umbrella, FileText, Monitor, Building, 
  Clock, MapPin, CheckCircle2, XCircle, AlertCircle, RefreshCw,
  Fingerprint, Mail, Phone, Briefcase, DollarSign, UserX, UserCheck, ArrowRightLeft
} from 'lucide-react';
import { toast } from 'sonner';
import EmployeeBiometricSync from '../components/EmployeeBiometricSync';

export default function EmployeeProfile() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [employee, setEmployee] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  
  // History data
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [leavesHistory, setLeavesHistory] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [justificationsHistory, setJustificationsHistory] = useState([]);
  const [departmentHistory, setDepartmentHistory] = useState([]);
  
  // Modals
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [deptChangeModalOpen, setDeptChangeModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [newDeptId, setNewDeptId] = useState('');
  const [deptChangeReason, setDeptChangeReason] = useState('');
  // Transfer modal
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferTargetCompanyId, setTransferTargetCompanyId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferCompanies, setTransferCompanies] = useState([]);

  useEffect(() => {
    fetchEmployee();
    fetchDepartments();
  }, [employeeId]);

  useEffect(() => {
    if (employee) {
      fetchAllHistories();
    }
  }, [employee]);

  const fetchEmployee = async () => {
    try {
      const response = await api.get(`/employees/${employeeId}/profile`);
      setEmployee(response.data);
    } catch (error) {
      toast.error('Employé non trouvé');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Erreur chargement directions:', error);
    }
  };

  const fetchAllHistories = async () => {
    try {
      const [attendance, leaves, logins, justifications, deptHist] = await Promise.all([
        api.get(`/employees/${employeeId}/attendance-history`).catch(() => ({ data: [] })),
        api.get(`/employees/${employeeId}/leaves-history`).catch(() => ({ data: [] })),
        api.get(`/employees/${employeeId}/login-history`).catch(() => ({ data: [] })),
        api.get(`/employees/${employeeId}/justifications-history`).catch(() => ({ data: [] })),
        api.get(`/employees/${employeeId}/department-history`).catch(() => ({ data: [] }))
      ]);
      
      setAttendanceHistory(attendance.data);
      setLeavesHistory(leaves.data);
      setLoginHistory(logins.data);
      setJustificationsHistory(justifications.data);
      setDepartmentHistory(deptHist.data);
    } catch (error) {
      console.error('Erreur chargement historiques:', error);
    }
  };

  const handleStatusChange = async () => {
    if (!newStatus) return;
    
    try {
      await api.post(`/employees/${employeeId}/change-status`, {
        status: newStatus,
        reason: statusReason
      });
      toast.success(`Statut changé en "${getStatusLabel(newStatus)}"`);
      setStatusModalOpen(false);
      setNewStatus('');
      setStatusReason('');
      fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const handleDeptChange = async () => {
    if (!newDeptId) return;
    
    try {
      await api.post(`/employees/${employeeId}/change-department`, {
        new_department_id: newDeptId,
        reason: deptChangeReason
      });
      toast.success('Direction changée avec succès');
      setDeptChangeModalOpen(false);
      setNewDeptId('');
      setDeptChangeReason('');
      fetchEmployee();
      fetchAllHistories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de direction');
    }
  };

  const openTransferModal = async () => {
    setTransferTargetCompanyId('');
    setTransferReason('');
    try {
      const res = await api.get('/companies');
      setTransferCompanies((res.data || []).filter(c => c.id !== employee?.company_id));
    } catch {
      setTransferCompanies([]);
    }
    setTransferModalOpen(true);
  };

  const handleTransfer = async () => {
    if (!transferTargetCompanyId) return;
    setTransferLoading(true);
    try {
      const res = await api.post(`/employees/${employeeId}/transfer`, {
        target_company_id: transferTargetCompanyId,
        reason: transferReason || null
      });
      toast.success(res.data?.message || 'Employé transféré avec succès');
      setTransferModalOpen(false);
      fetchEmployee();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du transfert');
    } finally {
      setTransferLoading(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Actif',
      suspended: 'Suspendu',
      terminated: 'Renvoyé'
    };
    return labels[status] || status;
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-amber-100 text-amber-800',
      terminated: 'bg-red-100 text-red-800'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100'}>
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canManageEmployee = ['super_admin', 'company_admin', 'hr', 'group_admin', 'group_hr'].includes(user?.role);
  const canTransfer = ['super_admin', 'group_admin', 'group_hr', 'hr'].includes(user?.role);

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  if (!employee) {
    return <div className="text-center py-8">Employé non trouvé</div>;
  }

  return (
    <div className="space-y-6" data-testid="employee-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>

      {/* Employee Info Card */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {employee.first_name} {employee.last_name}
                </h1>
                <p className="text-slate-600">{employee.position}</p>
                <div className="flex items-center gap-2 mt-2">
                  {getStatusBadge(employee.status || 'active')}
                  {employee.biometric_id ? (
                    <Badge className="bg-green-100 text-green-800">
                      <Fingerprint className="w-3 h-3 mr-1" />
                      Biométrie OK
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-800">
                      <Fingerprint className="w-3 h-3 mr-1" />
                      Biométrie non configurée
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            {canManageEmployee && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeptChangeModalOpen(true)}
                  data-testid="change-dept-button"
                >
                  <Building className="w-4 h-4 mr-2" />
                  Changer Direction
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStatusModalOpen(true)}
                  data-testid="change-status-button"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Changer Statut
                </Button>
                {canTransfer && (
                  <Button
                    variant="outline"
                    className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={openTransferModal}
                    data-testid="transfer-employee-button"
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Transférer
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-sm font-medium">{employee.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Téléphone</p>
                <p className="text-sm font-medium">{employee.phone || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Direction</p>
                <p className="text-sm font-medium">{employee.department_name || 'Non affecté'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Salaire</p>
                <p className="text-sm font-medium">{formatMoney(employee.base_salary, 'USD')}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for histories */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-7 gap-2">
          <TabsTrigger value="info" data-testid="tab-info">Infos</TabsTrigger>
          <TabsTrigger value="biometric" data-testid="tab-biometric">
            <Fingerprint className="w-4 h-4 mr-1" />
            Biométrie
          </TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Présences</TabsTrigger>
          <TabsTrigger value="leaves" data-testid="tab-leaves">Congés</TabsTrigger>
          <TabsTrigger value="logins" data-testid="tab-logins">Connexions</TabsTrigger>
          <TabsTrigger value="justifications" data-testid="tab-justifications">Justificatifs</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Directions</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Informations détaillées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Matricule</p>
                  <p className="font-medium">{employee.matricule}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">ID Biométrique</p>
                  <p className="font-medium">{employee.biometric_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Horaires</p>
                  <p className="font-medium">{employee.work_start_time} - {employee.work_end_time}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Solde congés</p>
                  <p className="font-medium">{employee.leave_balance} jours</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date création</p>
                  <p className="font-medium">{formatDate(employee.created_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Biometric Sync Tab */}
        <TabsContent value="biometric">
          <EmployeeBiometricSync employee={employee} onUpdate={fetchEmployee} />
        </TabsContent>

        {/* Attendance History Tab */}
        <TabsContent value="attendance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Historique des Présences
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Entrée</TableHead>
                    <TableHead>Sortie</TableHead>
                    <TableHead>Heures</TableHead>
                    <TableHead>Retard</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendanceHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500">
                        Aucune donnée de présence
                      </TableCell>
                    </TableRow>
                  ) : (
                    attendanceHistory.map((att) => (
                      <TableRow key={att.id}>
                        <TableCell>{att.date}</TableCell>
                        <TableCell>{att.check_in_time || '-'}</TableCell>
                        <TableCell>{att.check_out_time || '-'}</TableCell>
                        <TableCell>{att.hours_worked?.toFixed(1) || '-'}h</TableCell>
                        <TableCell>
                          {att.late_minutes > 0 ? (
                            <span className="text-amber-600">{att.late_minutes} min</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {att.is_absent ? (
                            <Badge className="bg-red-100 text-red-800">Absent</Badge>
                          ) : att.late_minutes > 0 ? (
                            <Badge className="bg-amber-100 text-amber-800">Retard</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaves History Tab */}
        <TabsContent value="leaves">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Umbrella className="w-5 h-5 text-primary" />
                Historique des Congés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Début</TableHead>
                    <TableHead>Fin</TableHead>
                    <TableHead>Jours</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Motif</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leavesHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500">
                        Aucune demande de congé
                      </TableCell>
                    </TableRow>
                  ) : (
                    leavesHistory.map((leave) => (
                      <TableRow key={leave.id}>
                        <TableCell className="capitalize">{leave.leave_type}</TableCell>
                        <TableCell>{leave.start_date}</TableCell>
                        <TableCell>{leave.end_date}</TableCell>
                        <TableCell>{leave.days_count}</TableCell>
                        <TableCell>
                          {leave.status === 'approved' ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Approuvé
                            </Badge>
                          ) : leave.status === 'rejected' ? (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="w-3 h-3 mr-1" />
                              Refusé
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800">
                              <Clock className="w-3 h-3 mr-1" />
                              En attente
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{leave.reason || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Login History Tab */}
        <TabsContent value="logins">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5 text-primary" />
                Historique des Connexions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date/Heure</TableHead>
                    <TableHead>Adresse IP</TableHead>
                    <TableHead>Équipement</TableHead>
                    <TableHead>Navigateur</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loginHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500">
                        Aucun historique de connexion
                      </TableCell>
                    </TableRow>
                  ) : (
                    loginHistory.map((login) => (
                      <TableRow key={login.id}>
                        <TableCell>{formatDate(login.login_at)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-slate-400" />
                            {login.ip_address || '-'}
                          </div>
                        </TableCell>
                        <TableCell>{login.device_info || '-'}</TableCell>
                        <TableCell className="max-w-xs truncate text-xs">
                          {login.user_agent?.substring(0, 50) || '-'}
                        </TableCell>
                        <TableCell>
                          {login.success ? (
                            <Badge className="bg-green-100 text-green-800">Réussi</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Échec</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Justifications History Tab */}
        <TabsContent value="justifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Historique des Justificatifs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Document</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {justificationsHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500">
                        Aucun justificatif soumis
                      </TableCell>
                    </TableRow>
                  ) : (
                    justificationsHistory.map((just) => (
                      <TableRow key={just.id}>
                        <TableCell>{formatDate(just.created_at)}</TableCell>
                        <TableCell className="capitalize">{just.type || 'absence'}</TableCell>
                        <TableCell className="max-w-xs truncate">{just.reason || '-'}</TableCell>
                        <TableCell>
                          {just.status === 'approved' ? (
                            <Badge className="bg-green-100 text-green-800">Validé</Badge>
                          ) : just.status === 'rejected' ? (
                            <Badge className="bg-red-100 text-red-800">Refusé</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800">En attente</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {just.document_url ? (
                            <a href={just.document_url} target="_blank" rel="noopener noreferrer" 
                               className="text-primary hover:underline">
                              Voir document
                            </a>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Department History Tab */}
        <TabsContent value="departments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                Historique des Affectations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Direction</TableHead>
                    <TableHead>Date affectation</TableHead>
                    <TableHead>Date fin</TableHead>
                    <TableHead>Durée</TableHead>
                    <TableHead>Motif</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {departmentHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500">
                        Aucun historique d'affectation
                      </TableCell>
                    </TableRow>
                  ) : (
                    departmentHistory.map((hist) => (
                      <TableRow key={hist.id}>
                        <TableCell className="font-medium">{hist.department_name}</TableCell>
                        <TableCell>{formatDate(hist.assigned_at)}</TableCell>
                        <TableCell>{hist.ended_at ? formatDate(hist.ended_at) : 'Actuel'}</TableCell>
                        <TableCell>
                          {hist.ended_at 
                            ? Math.ceil((new Date(hist.ended_at) - new Date(hist.assigned_at)) / (1000 * 60 * 60 * 24)) + ' jours'
                            : 'En cours'}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{hist.reason || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Status Change Modal */}
      <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Changer le statut de l'employé</DialogTitle>
            <DialogDescription>
              Cette action affectera l'accès de l'employé à son compte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nouveau statut</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  type="button"
                  variant={newStatus === 'active' ? 'default' : 'outline'}
                  onClick={() => setNewStatus('active')}
                  className={newStatus === 'active' ? 'bg-green-600' : ''}
                >
                  <UserCheck className="w-4 h-4 mr-1" />
                  Actif
                </Button>
                <Button
                  type="button"
                  variant={newStatus === 'suspended' ? 'default' : 'outline'}
                  onClick={() => setNewStatus('suspended')}
                  className={newStatus === 'suspended' ? 'bg-amber-600' : ''}
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  Suspendu
                </Button>
                <Button
                  type="button"
                  variant={newStatus === 'terminated' ? 'default' : 'outline'}
                  onClick={() => setNewStatus('terminated')}
                  className={newStatus === 'terminated' ? 'bg-red-600' : ''}
                >
                  <UserX className="w-4 h-4 mr-1" />
                  Renvoyé
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="statusReason">Motif (optionnel)</Label>
              <textarea
                id="statusReason"
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2"
                rows={3}
                placeholder="Raison du changement de statut..."
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStatusModalOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleStatusChange} className="flex-1 bg-primary" disabled={!newStatus}>
                Confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Department Change Modal */}
      <Dialog open={deptChangeModalOpen} onOpenChange={setDeptChangeModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Changer de direction</DialogTitle>
            <DialogDescription>
              Affectez l'employé à une nouvelle direction.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nouvelle direction</Label>
              <select
                value={newDeptId}
                onChange={(e) => setNewDeptId(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2"
              >
                <option value="">-- Sélectionner une direction --</option>
                {departments.filter(d => d.is_active !== false).map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deptReason">Motif du changement</Label>
              <textarea
                id="deptReason"
                value={deptChangeReason}
                onChange={(e) => setDeptChangeReason(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2"
                rows={3}
                placeholder="Raison du changement de direction..."
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeptChangeModalOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleDeptChange} className="flex-1 bg-primary" disabled={!newDeptId}>
                Confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Modal */}
      <Dialog open={transferModalOpen} onOpenChange={(open) => { if (!transferLoading) setTransferModalOpen(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transférer l'employé
            </DialogTitle>
            <DialogDescription>
              Transfert de <strong>{employee?.first_name} {employee?.last_name}</strong> vers une autre entreprise.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Résumé conservé / réinitialisé */}
            <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-1">
              <p className="font-medium text-slate-700 mb-2">Ce qui sera conservé :</p>
              <p className="text-green-700">✓ Empreintes biométriques</p>
              <p className="text-green-700">✓ Historique des présences</p>
              <p className="text-green-700">✓ Salaire et avantages</p>
              <p className="text-green-700">✓ Contrat et informations personnelles</p>
              <p className="text-green-700">✓ Accès à l'application</p>
              <p className="text-amber-600 mt-2">⚠ Département : sera réinitialisé</p>
            </div>

            {/* Sélection entreprise cible */}
            <div className="space-y-1">
              <Label htmlFor="transfer-company-profile">
                Entreprise de destination <span className="text-red-500">*</span>
              </Label>
              {transferCompanies.length === 0 ? (
                <p className="text-sm text-slate-500 italic">Aucune autre entreprise disponible.</p>
              ) : (
                <select
                  id="transfer-company-profile"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={transferTargetCompanyId}
                  onChange={(e) => setTransferTargetCompanyId(e.target.value)}
                >
                  <option value="">-- Sélectionner une entreprise --</option>
                  {transferCompanies.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Raison */}
            <div className="space-y-1">
              <Label htmlFor="transfer-reason-profile">Raison du transfert (optionnel)</Label>
              <textarea
                id="transfer-reason-profile"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={3}
                placeholder="Ex : mutation, restructuration..."
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setTransferModalOpen(false)}
              disabled={transferLoading}
            >
              Annuler
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={handleTransfer}
              disabled={!transferTargetCompanyId || transferLoading}
            >
              {transferLoading ? 'Transfert en cours...' : 'Confirmer le transfert'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
